/**
 * Product Media Gallery - Carousel & Viewer functionality
 * Uses shared MediaViewer for fullscreen viewing
 */
(function() {
  var galleryData = new Map();
  
  // ===== Media Viewer Integration =====
  
  function initViewer(gallery) {
    var viewer = gallery.querySelector('.media-viewer');
    if (!viewer) return;
    
    // Get all media items but deduplicate by media-id (mobile + desktop have same items)
    var mediaItems = gallery.querySelectorAll('[data-media-id][data-media-type]');
    var seenIds = new Set();
    var allMedia = [];
    var mediaDataMap = {};
    
    mediaItems.forEach(function(item) {
      var mediaId = item.getAttribute('data-media-id');
      if (!seenIds.has(mediaId)) {
        seenIds.add(mediaId);
        
        var type = item.getAttribute('data-media-type');
        var img = item.querySelector('img');
        var video = item.querySelector('video');
        
        var mediaData = {
          id: mediaId,
          type: type,
          imageSrc: img ? (img.getAttribute('data-lightbox-src') || img.currentSrc || img.src) : null,
          alt: img ? img.alt : '',
          videoSrc: null,
          poster: null
        };
        
        if (video) {
          var source = video.querySelector('source');
          mediaData.videoSrc = source ? source.src : video.src;
          mediaData.poster = video.poster || '';
        }
        
        allMedia.push(mediaData);
        mediaDataMap[mediaId] = allMedia.length - 1;
      }
    });
    
    // Helper to find index by media ID
    function getMediaIndex(mediaId) {
      return mediaDataMap[mediaId] !== undefined ? mediaDataMap[mediaId] : 0;
    }
    
    // Event listeners for ALL media items (including duplicates from mobile/desktop)
    mediaItems.forEach(function(item) {
      var mediaId = item.getAttribute('data-media-id');
      var type = item.getAttribute('data-media-type');
      
      // For videos, the play button should open the viewer
      var playBtn = item.querySelector('[data-play-btn]');
      if (playBtn) {
        playBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          openViewer(getMediaIndex(mediaId));
        });
      }
      
      // For images, clicking anywhere opens the viewer
      if (type === 'image') {
        item.addEventListener('click', function() {
          openViewer(getMediaIndex(mediaId));
        });
      }
      
      // On mobile, clicking video wrapper also opens viewer
      item.addEventListener('click', function(e) {
        if (window.innerWidth < 990 && (type === 'video' || type === 'external_video')) {
          openViewer(getMediaIndex(mediaId));
        }
      });
    });
    
    function openViewer(index) {
      if (typeof window.MediaViewer !== 'undefined') {
        window.MediaViewer.open({
          viewer: viewer,
          mediaItems: allMedia,
          startIndex: index
        });
      }
    }
  }
  
  // ===== Carousel Functionality =====
  
  function initGalleryCarousels() {
    var galleries = document.querySelectorAll('.product-media-gallery[data-enable-carousel="true"]');
    
    galleries.forEach(function(gallery) {
      var mediaCount = parseInt(gallery.getAttribute('data-media-count'), 10);
      if (mediaCount <= 1) return;
      
      var carouselContainer = gallery.querySelector('[data-gallery-carousel]');
      if (!carouselContainer) return;
      
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            startCarousel(gallery, carouselContainer);
          } else {
            stopCarousel(gallery, carouselContainer);
          }
        });
      }, { threshold: 0.5 });
      
      observer.observe(carouselContainer);
    });
  }
  
  function startCarousel(gallery, container) {
    var mediaItems = container.querySelectorAll('.product-media-gallery__carousel-item');
    var pill = container.querySelector('.product-media-gallery__progress-pill');
    var mediaCount = parseInt(gallery.getAttribute('data-media-count'), 10);
    var imageDuration = (parseInt(gallery.getAttribute('data-carousel-duration'), 10) || 3) * 1000;
    
    if (mediaCount <= 1) return;
    if (galleryData.has(container)) return;
    
    var currentIndex = 0;
    var timeoutId = null;
    var videoEndedHandler = null;
    var isPaused = false;
    var resumeTimeoutId = null;
    
    function setPillDuration(durationMs) {
      if (pill) pill.style.setProperty('--pill-duration', (durationMs / 1000) + 's');
    }
    
    function restartPillAnimation() {
      if (pill) {
        pill.classList.remove('is-filling');
        void pill.offsetWidth;
        pill.classList.add('is-filling');
      }
    }
    
    function pausePillAnimation() {
      if (pill) pill.classList.add('is-paused');
    }
    
    function resumePillAnimation() {
      if (pill) pill.classList.remove('is-paused');
    }
    
    function nextSlide() {
      if (isPaused) return;
      var nextIndex = (currentIndex + 1) % mediaCount;
      showSlide(nextIndex, false);
    }
    
    function pauseCarousel() {
      isPaused = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      pausePillAnimation();
      
      var currentItem = mediaItems[currentIndex];
      var video = currentItem ? currentItem.querySelector('video') : null;
      if (video) video.pause();
    }
    
    function resumeCarousel() {
      isPaused = false;
      resumePillAnimation();
      
      var currentItem = mediaItems[currentIndex];
      var video = currentItem ? currentItem.querySelector('video') : null;
      if (video) {
        video.play().catch(function() {});
      } else {
        scheduleNextSlide();
      }
    }
    
    function scheduleNextSlide() {
      if (isPaused) return;
      
      var currentItem = mediaItems[currentIndex];
      var video = currentItem ? currentItem.querySelector('video') : null;
      var isVideo = video !== null;
      
      if (videoEndedHandler) {
        mediaItems.forEach(function(item) {
          var v = item.querySelector('video');
          if (v) v.removeEventListener('ended', videoEndedHandler);
        });
      }
      
      if (isVideo) {
        videoEndedHandler = function() {
          if (!isPaused) nextSlide();
        };
        video.addEventListener('ended', videoEndedHandler, { once: true });
        
        if (video.duration && !isNaN(video.duration)) {
          setPillDuration(video.duration * 1000);
          restartPillAnimation();
        } else {
          video.addEventListener('loadedmetadata', function() {
            if (video.duration && !isNaN(video.duration)) {
              setPillDuration(video.duration * 1000);
              restartPillAnimation();
            }
          }, { once: true });
          setPillDuration(imageDuration);
          restartPillAnimation();
        }
      } else {
        setPillDuration(imageDuration);
        restartPillAnimation();
        timeoutId = setTimeout(nextSlide, imageDuration);
      }
    }
    
    function showSlide(newIndex, isUserInteraction) {
      var prevIndex = currentIndex;
      currentIndex = newIndex;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      mediaItems.forEach(function(item, i) {
        var video = item.querySelector('video');
        
        if (i === prevIndex && i !== newIndex) {
          item.classList.add('is-leaving');
          item.classList.remove('is-active');
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        } else if (i === newIndex) {
          item.classList.remove('is-leaving');
          item.classList.add('is-active');
          if (video && !isPaused) {
            video.currentTime = 0;
            video.play().catch(function() {});
          }
        } else {
          item.classList.remove('is-active', 'is-leaving');
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        }
      });
      
      setTimeout(function() {
        mediaItems.forEach(function(item) {
          item.classList.remove('is-leaving');
        });
      }, 600);
      
      var thumbnails = gallery.querySelectorAll('.product-media-gallery__thumbnail');
      thumbnails.forEach(function(thumb, i) {
        thumb.classList.toggle('is-active', i === newIndex);
      });
      
      if (!isPaused) scheduleNextSlide();
    }
    
    function handleUserInteraction() {
      pauseCarousel();
      if (resumeTimeoutId) clearTimeout(resumeTimeoutId);
      resumeTimeoutId = setTimeout(function() {
        resumeCarousel();
      }, 5000);
    }
    
    container.addEventListener('mouseenter', pauseCarousel);
    container.addEventListener('mouseleave', function() {
      if (resumeTimeoutId) clearTimeout(resumeTimeoutId);
      resumeCarousel();
    });
    container.addEventListener('touchstart', handleUserInteraction, { passive: true });
    container.addEventListener('click', handleUserInteraction);
    
    var firstItem = mediaItems[0];
    var firstVideo = firstItem ? firstItem.querySelector('video') : null;
    
    firstItem.classList.add('is-active');
    
    if (firstVideo) {
      firstVideo.currentTime = 0;
      firstVideo.play().catch(function() {});
    }
    
    scheduleNextSlide();
    
    galleryData.set(container, {
      currentIndex: currentIndex,
      showSlide: showSlide,
      cleanup: function() {
        if (timeoutId) clearTimeout(timeoutId);
        if (resumeTimeoutId) clearTimeout(resumeTimeoutId);
        if (videoEndedHandler) {
          mediaItems.forEach(function(item) {
            var video = item.querySelector('video');
            if (video) video.removeEventListener('ended', videoEndedHandler);
          });
        }
        container.removeEventListener('mouseenter', pauseCarousel);
        container.removeEventListener('mouseleave', resumeCarousel);
      }
    });
    
    var thumbnails = gallery.querySelectorAll('.product-media-gallery__thumbnail');
    thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        var index = parseInt(thumb.getAttribute('data-index'), 10);
        if (!isNaN(index)) {
          handleUserInteraction();
          showSlide(index, true);
        }
      });
    });
  }
  
  function stopCarousel(gallery, container) {
    var data = galleryData.get(container);
    if (data) {
      if (data.cleanup) data.cleanup();
      galleryData.delete(container);
    }
    
    var mediaItems = container.querySelectorAll('.product-media-gallery__carousel-item');
    var pill = container.querySelector('.product-media-gallery__progress-pill');
    
    mediaItems.forEach(function(item, i) {
      var video = item.querySelector('video');
      item.classList.remove('is-leaving');
      item.classList.toggle('is-active', i === 0);
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    var thumbnails = gallery.querySelectorAll('.product-media-gallery__thumbnail');
    thumbnails.forEach(function(thumb, i) {
      thumb.classList.toggle('is-active', i === 0);
    });
    
    if (pill) {
      pill.classList.remove('is-filling');
      pill.style.removeProperty('--pill-duration');
    }
  }
  
  function initAllGalleries() {
    var allGalleries = document.querySelectorAll('.product-media-gallery');
    allGalleries.forEach(function(gallery) {
      initViewer(gallery);
    });
    initGalleryCarousels();
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }
})();
